import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { withRouter } from '../../hocs/withRouter';
import i18n from 'i18next';
import moment from 'moment';
import {
  DEFAULT_IMAP_PORT,
  DEFAULT_IMAP_SSL,
  DEFAULT_SMTP_PORT,
  DEFAULT_SMTP_SSL,
  DEFAULT_SMTP_TLS,
  login
} from '../../services/application';
import Button from '../buttons/button';
import LoginSnackbar from './login-snackbar';
import TextField from '../form/text-field/text-field';
import Spinner from '../spinner/spinner';
import { validateEmail } from '../../services/validation';
import './login.scss';
import { PROVIDER } from '../../constants';
import { resetDefaultAccount } from '../../services/accounts';
import { parseJwt } from '../../services/jwt';
import ACTIONS from '../../actions/lexon';
import CU_ACTIONS from '../../actions/user';
import { SwitchComponent } from '@syncfusion/ej2-react-buttons';

/**
 * Returns a Login component valid state from the current URL params
 *
 * @param {URLSearchParams} params
 * @returns {{values: {serverHost: string, serverPort: number, user: string, password: string, imapSsl: boolean, smtpHost: string, smtpPort: number, smtpSsl: boolean}, advanced: boolean}}
 */
const stateFromParams = params => ({
  values: {
    serverHost: params.has('serverHost') ? params.get('serverHost') : '',
    serverPort: params.has('serverPort')
      ? params.get('serverPort').replace(/[^0-9]*/g, '')
      : DEFAULT_IMAP_PORT,
    user: params.has('user') ? params.get('user') : '',
    password: '',
    imapSsl: params.has('imapSsl')
      ? params.get('imapSsl') === 'true'
      : DEFAULT_IMAP_SSL,
    smtpHost: params.has('smtpHost') ? params.get('smtpHost') : '',
    smtpPort: params.has('smtpPort')
      ? params.get('smtpPort').replace(/[^0-9]*/g, '')
      : DEFAULT_SMTP_PORT,
    smtpSsl: params.has('smtpSsl')
      ? params.get('smtpSsl') === 'true'
      : DEFAULT_SMTP_SSL,
    smtpTls: params.has('smtpTls')
      ? params.get('smtpTls') === 'true'
      : DEFAULT_SMTP_TLS
  },
  advanced: false
});

const stateFromFormValues = formValues => ({
  values: { ...formValues, password: '' },
  advanced: false
});

const stateFromAccount = account => ({
  values: {
    serverHost: account.serverHost || account.imap || '',
    serverPort: account.serverPort || account.imapPort || DEFAULT_IMAP_PORT,
    user: account.user || account.imapUser || '',
    password: account.password || account.imapPass || '',
    imapSsl:
      account && account.imapSsl !== undefined && account.imapSsl !== null
        ? account.imapSsl
        : DEFAULT_IMAP_SSL,
    smtpHost: account.smtpHost || account.smtp || '',
    smtpPort: account.smtpPort || DEFAULT_SMTP_PORT,
    smtpSsl:
      account && account.smtpSsl !== undefined && account.smtpSsl !== null
        ? account.smtpSsl
        : DEFAULT_SMTP_SSL,
    smtpTls:
      account && account.smtpTls !== undefined && account.smtpTls !== null
        ? account.smtpTls
        : DEFAULT_SMTP_TLS
  },
  advanced: false
});

export class Login extends Component {
  constructor(props) {
    super(props);

    this.state = {
      values: {
        serverHost: '',
        serverPort: DEFAULT_IMAP_PORT,
        user: '',
        password: '',
        imapSsl: DEFAULT_IMAP_SSL,
        smtpHost: '',
        smtpPort: DEFAULT_SMTP_PORT,
        smtpSsl: DEFAULT_SMTP_SSL,
        smtpTls: DEFAULT_SMTP_TLS
      },
      advanced: false,
      focusedComponentId: null
    };

    const urlParams = new URLSearchParams(this.props.location.search);
    if (urlParams.toString()) {
      const paramsState = stateFromParams(urlParams);
      this.state = { ...this.state, ...paramsState };
    }

    if (
      this.props.formValues &&
      Object.keys(this.props.formValues).length > 0
    ) {
      const formState = stateFromFormValues(this.props.formValues);
      this.state = { ...this.state, ...formState };
    }

    this.onFieldChange = this.onFieldChange.bind(this);
    this.login = this.login.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.serverHostRef = React.createRef();
    this.serverPortRef = React.createRef();
    this.userRef = React.createRef();
    this.passwordRef = React.createRef();
    this.smtpHostRef = React.createRef();
    this.smtpPortRef = React.createRef();
  }

  async componentDidMount() {
    this.originalBodyStyles = {
      overflow: document.body.style.overflow,
      position: document.body.style.position
    };
    document.body.style.setProperty('overflow', 'auto', 'important');
    document.body.style.setProperty('position', 'inherit', 'important');

    const { account, userId, lexonToken, idClientLef } = this.props.lexon;
    const appConnect = new URLSearchParams(window.location.search).get(
      'appConnectMail'
    );
    const appConnectParse = appConnect && parseJwt(appConnect);
    const token = appConnect ? appConnect : lexonToken;

    let url = '';

    if (appConnectParse /*&& window !== window.top*/) {
      url = `${window.API_GATEWAY_ACC}/api/v2/accounts/auth/usermail/${appConnectParse.idClienteNavision}/account/default`;
    } else {
      url = `${window.API_GATEWAY_ACC}/api/v2/accounts/auth/usermail/${userId}/account/imap/${account}`;
    }

    var myHeaders = new Headers();
    myHeaders.append('Authorization', 'Bearer ' + token);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: myHeaders
      });
      const data = await response.json();
      if (data && data.data && data.data.configAccount) {
        const newValues = stateFromAccount(data.data.configAccount);
        this.setState({ values: newValues.values }, () => {
          this.setState({ values: newValues.values }, () => {
             this.props.dispatchLogin({
              ...this.state.values,
              userId,
              lexonToken: token,
              idClientLef: idClientLef
            });
          });
        });
      }
    } catch (error) {
      console.error('Debug: Error fetching account config:', error);
    }
  }

  componentWillUnmount() {
    if (this.originalBodyStyles) {
      document.body.style.overflow = this.originalBodyStyles.overflow;
      document.body.style.position = this.originalBodyStyles.position;
    }
  }

  goBack() {
    if (typeof this.props.lexon !== 'undefined') {
      const { userId, token, lexonToken } = this.props.lexon;
      if (lexonToken !== null) {
        resetDefaultAccount(userId, lexonToken)
          .then(() => {
            const urlRedirect = token
              ? `${window.URL_SELECT_ACCOUNT}/access/${lexonToken}/`
              : `${window.URL_SELECT_ACCOUNT}/user/${userId}/encrypt/0`;
            window.open(urlRedirect, '_self');
          })
          .catch(error => {
            console.error('error =>', error);
          });
      } else {
          window.open(window.URL_SELECT_ACCOUNT, "_self");
      }
    }
  }

  refreshPage() {
    window.location.reload();
  }

  render() {
    const {
      serverHost,
      serverPort,
      user,
      password,
      imapSsl,
      smtpHost,
      smtpPort,
      smtpSsl,
      smtpTls
    } = this.state.values;

    if (this.props.application.user.credentials) {
      if (this.props.application.service === 'calendar') {
        return <Navigate to="/calendar" />;
      }
      return <Navigate to="/" />;
    }

    return (
      <div className={`login--background`}>
        <header className={`login-header`}>
          <img
            src="/assets/images/new-desing/logo-Lefebvre-2025.svg"
            alt="LEFEBVRE LOGO"
            className={`logo-lefebvre-header`}
            height="38px"
            onClick={this.refreshPage}
          />
        </header>
        <div className={`login--container`}>
          <Spinner
            visible={this.props.application.activeRequests > 0}
            className={`spinner`}
            pathClassName={`spinnerPath`}
          />
          <div className={`card`}>
            <header>
              <div className={`subtitle`}>{i18n.t('login.Login')}</div>
            </header>
            <form onSubmit={this.login} noValidate>
              <div className={`server`}>
                <TextField
                  id="serverHost"
                  fieldClass={`formField serverHost`}
                  value={serverHost}
                  onChange={this.onFieldChange}
                  focused={this.isFocused('serverHost')}
                  required={true}
                  autoComplete="on"
                  label={i18n.t('login.Host')}
                  ref={this.serverHostRef}
                />
                <TextField
                  key="serverPort"
                  id="serverPort"
                  fieldClass={`formField serverPort`}
                  min="0"
                  value={serverPort}
                  onChange={this.onFieldChange}
                  focused={this.isFocused('serverPort')}
                  required={true}
                  autoComplete="on"
                  label={i18n.t('login.Port')}
                  ref={this.serverPortRef}
                />
              </div>
              <TextField
                id="user"
                text="text"
                fieldClass={`formField fullWidth`}
                value={user}
                onChange={this.onFieldChange}
                focused={this.isFocused('user')}
                required={true}
                autoComplete="on"
                label={i18n.t('login.User')}
                ref={this.userRef}
              />
              <TextField
                id="password"
                type={'password'}
                fieldClass={`formField fullWidth`}
                value={password}
                onChange={this.onFieldChange}
                focused={this.isFocused('password')}
                required={true}
                label={i18n.t('login.Password')}
                ref={this.passwordRef}
              />
              <div className={`advancedContainer`}>
                <div className={`switchContainer`}>
                  <SwitchComponent
                    id="imapSsl"
                    checked={imapSsl}
                    label={i18n.t('login.ImapSSL')}
                    change={() => this.onToggle('imapSsl')}
                  />
                  <label htmlFor="imapSsl" className={`switchLabel`}>
                    IMAP SSL
                  </label>
                </div>
                <div className={`server`}>
                  <TextField
                    id="smtpHost"
                    fieldClass={`formField fullWidth serverHost`}
                    value={smtpHost}
                    onChange={this.onFieldChange}
                    focused={this.isFocused('smtpHost')}
                    label={i18n.t('login.Host')}
                    ref={this.smtpHostRef}
                  />
                  <TextField
                    id="smtpPort"
                    fieldClass={`formField fullWidth serverPort`}
                    min="0"
                    required={true}
                    value={smtpPort}
                    onChange={this.onFieldChange}
                    focused={this.isFocused('smtpPort')}
                    label={i18n.t('login.Port')}
                    ref={this.smtpPortRef}
                  />
                </div>
                <div className={`advancedSwitchContainer`}>
                  <div className={`switchContainer`}>
                    <SwitchComponent
                      id="smtpSsl"
                      checked={smtpSsl}
                      label={i18n.t('login.SmtpSSL')}
                      change={() => this.onToggle('smtpSsl')}
                    />
                    <label htmlFor="smtpSsl" className={`switchLabel`}>
                      SMTP SSL
                    </label>
                  </div>
                  <div className={`switchContainer`}>
                    <SwitchComponent
                      id="smtpTls"
                      checked={smtpTls}
                      label={i18n.t('login.SmtpTLS')}
                      change={() => this.onToggle('smtpTls')}
                    />
                    <label htmlFor="smtpTls" className={`switchLabel`}>
                      SMTP TLS
                    </label>
                  </div>
                </div>
              </div>
              <div className={`button-section`}>
                <Button
                  onClick={() => {
                    this.goBack();
                  }}
                  className={`CancelButton`}
                  label={i18n.t('login.actions.Cancel')}
                />
                <Button
                  type={'submit'}
                  className={`loginButton`}
                  label={i18n.t('login.actions.Login')}
                />
              </div>
            </form>
          </div>
          <LoginSnackbar />
        </div>
        <div className={`connections-help`}>
          <p className={`need-help`}>{i18n.t('login.needHelp')}</p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <p className={`client`}>{i18n.t('login.phoneNumber')}</p>
            <a
              className={`email-link`}
              href={`mailto:${i18n.t('login.client')}`}
            >
              {i18n.t('login.client')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  isFocused(componentId) {
    return componentId === this.state.focusedComponentId;
  }

  onToggle(id) {
    this.setState(prevState => {
      const newState = { ...prevState };
      newState.values = { ...prevState.values };
      newState.values[id] = !newState.values[id];
      if (id === 'smtpSsl' && newState.values[id] === true) {
        newState.values.smtpTls = false;
      } else if (id === 'smtpTls' && newState.values[id] === true) {
        newState.values.smtpSsl = false;
      }
      return newState;
    });
  }

  onFieldChange(event) {
    const target = event.target;
    this.setState(prevState => {
      const newState = { ...prevState };
      newState.focusedComponentId = target.id;
      newState.values = { ...prevState.values };
      newState.values[target.id] = target.value;
      return newState;
    });
  }

  toggleAdvanced(event) {
    event.preventDefault();
    event.target.blur();
    this.setState({ advanced: !this.state.advanced });
  }

  validateEmail() {
    const element = document.getElementById('user');
    const error = validateEmail(this.state.values.user);
    if (error) {
      element.setCustomValidity(error);
      setTimeout(() => element.reportValidity());
      return false;
    }
    return true;
  }

  login(event) {
    event.preventDefault();
    const isServerHostValid = this.serverHostRef.current?.validate() ?? true;
    const isServerPortValid = this.serverPortRef.current?.validate() ?? true;
    const isUserValid = this.userRef.current?.validate() ?? true;
    const isPasswordValid = this.passwordRef.current?.validate() ?? true;
    const isSmtpPortValid = this.smtpPortRef.current?.validate() ?? true;

    const isFormValid =
      isServerHostValid &&
      isServerPortValid &&
      isUserValid &&
      isPasswordValid &&
      isSmtpPortValid;

    if (isFormValid) {
      const { userId, lexonToken, idClientLef } = this.props.lexon;
      this.saveLoginConfig();
      this.props.dispatchLogin({
        ...this.state.values,
        userId,
        lexonToken,
        idClientLef
      });
    }
  }

  async saveLoginConfig() {
    let { userId = '', account = this.state.values.user } = this.props.lexon;
    if (account === '' || account === null) {
      account = this.state.values.user;
    }
    const url = `${window.API_GATEWAY_ACC}/api/v2/accounts/auth/usermail/${userId}/account/${PROVIDER}/${account}/config/addorupdate`;
    await fetch(url, {
      method: 'POST',
      headers: {
        'content-Type': 'application/json',
        Authorization: 'Bearer ' + this.props.lexon.lexonToken
      },
      body: JSON.stringify({
        imap: this.state.values.serverHost,
        imapPort: this.state.values.serverPort,
        imapUser: this.state.values.user,
        imapPass: this.state.values.password,
        imapSsl: this.state.values.imapSsl,
        smtp: this.state.values.smtpHost,
        smtpPort: this.state.values.smtpPort,
        smtpSsl: this.state.values.smtpSsl,
        smtpTls: this.state.values.smtpTls
      })
    });
    const date = new Date();
    const lastConnection = moment(date).format('DD/MM/YYYY - HH:mm');
    this.props.setLastConnection(lastConnection);
    this.props.setAccount(account);
  }
}

const mapStateToProps = state => ({
  application: state.application,
  formValues: state.login.formValues,
  lexon: state.lexon
});

const mapDispatchToProps = dispatch => ({
  dispatchLogin: credentials => login(dispatch, credentials),
  setAccount: account => dispatch(ACTIONS.setAccount(account)),
  setLastConnection: lastConnection =>
    dispatch(CU_ACTIONS.setLastConnection(lastConnection))
});

const ConnectedLogin = connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(Login));

export default ConnectedLogin;
